import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Empty,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import PageHeader from "../../../component/pageHeaders";
import { API } from "../../../config/API";
import { GET } from "../../../utils/apirequest";
import { zones } from "../../../config/zones";
import { useEpaperConfig } from "../../../config/epaperConfig";
import DatePresetFilter from "../components/DatePresetFilter";

const { Option } = Select;

const CHART_COLORS = [
  "#1890ff", "#52c41a", "#fa8c16", "#f5222d", "#722ed1",
  "#13c2c2", "#eb2f96", "#faad14", "#a0d911", "#2f54eb",
  "#08979c", "#cf1322", "#d46b08", "#7cb305", "#1d39c4",
];

const THIS_WEEK: [dayjs.Dayjs, dayjs.Dayjs] = [dayjs().startOf("week"), dayjs().endOf("week")];
const ALL_TIME_START = dayjs("2026-01-01").startOf("day");

function toDateParams(dr: [dayjs.Dayjs, dayjs.Dayjs] | null) {
  const range = dr ?? [ALL_TIME_START, dayjs().endOf("day")];
  return {
    startDate: range[0].startOf("day").toISOString(),
    endDate: range[1].endOf("day").toISOString(),
  };
}

// ──────────────────────────── Types ─────────────────────────────

interface ZoneWiseItem {
  zone: string;
  count: number;
}

interface DistrictWiseItem {
  district: string;
  zone: string;
  count: number;
}

interface DailyItem {
  date: string;
  count: number;
}

interface TableDistrictRow {
  zone: string;
  district: string;
  countBefore: number;
  dailyCounts: Record<string, number>;
  districtTotal: number;
}

interface TableApiResponse {
  dateRange: { startDate: string; endDate: string };
  data: TableDistrictRow[];
  zoneWiseTotals: Record<string, number>;
}

interface ProcessedRow {
  key: string;
  zone: string;
  district: string;
  countBefore: number;
  dailyCounts: Record<string, number>;
  districtTotal: number;
  isZoneTotal?: boolean;
  zoneTotal?: number;
  zoneRowSpan: number;
}

// ──────────────────────────── Component ─────────────────────────

const EpaperUsersReports: React.FC = () => {
  // Zone filter options come from E-Paper Settings.
  useEpaperConfig();
  const navigate = useNavigate();

  // Date field mode: registration = eu.created_at, subscription = latest active subscription created_at
  const [dateField, setDateField] = useState<'registration' | 'subscription'>('subscription');

  // Per-chart date ranges (null = All time)
  const [dailyDateRange, setDailyDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(THIS_WEEK);
  const [zoneWiseDateRange, setZoneWiseDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(THIS_WEEK);
  const [districtWiseDateRange, setDistrictWiseDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(THIS_WEEK);

  // Zone filter for district chart
  const [districtZoneFilter, setDistrictZoneFilter] = useState<string>("");

  // Chart data
  const [zoneWiseData, setZoneWiseData] = useState<ZoneWiseItem[]>([]);
  const [districtWiseData, setDistrictWiseData] = useState<DistrictWiseItem[]>([]);
  const [dailyData, setDailyData] = useState<DailyItem[]>([]);

  // Chart loading states
  const [zoneLoading, setZoneLoading] = useState(false);
  const [districtLoading, setDistrictLoading] = useState(false);
  const [dailyLoading, setDailyLoading] = useState(false);

  // Table state (separate date filter)
  const [tableDateRange, setTableDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(THIS_WEEK);
  const [tableData, setTableData] = useState<TableApiResponse | null>(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [zoneExportLoading, setZoneExportLoading] = useState(false);
  const [districtExportLoading, setDistrictExportLoading] = useState(false);
  const [dailyExportLoading, setDailyExportLoading] = useState(false);

  // ──────────────────── Fetch helpers ────────────────────────────

  const fetchZoneWise = useCallback(async (
    dr: [dayjs.Dayjs, dayjs.Dayjs] | null,
    df?: string
  ) => {
    setZoneLoading(true);
    try {
      const params = new URLSearchParams({ ...toDateParams(dr), dateField: df ?? dateField });
      const res: any = await GET(
        `${API.EPAPER_USERS_REPORT_ZONE_WISE}?${params}`,
        null, null
      );
      setZoneWiseData(res?.data ?? []);
    } catch {
      setZoneWiseData([]);
    } finally {
      setZoneLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateField]);

  const fetchDistrictWise = useCallback(
    async (dr: [dayjs.Dayjs, dayjs.Dayjs] | null, zone: string, df?: string) => {
      setDistrictLoading(true);
      try {
        const params = new URLSearchParams({
          ...toDateParams(dr),
          dateField: df ?? dateField,
          ...(zone ? { zone } : {}),
        });
        const res: any = await GET(
          `${API.EPAPER_USERS_REPORT_DISTRICT_WISE}?${params}`, null, null
        );
        setDistrictWiseData(res?.data ?? []);
      } catch {
        setDistrictWiseData([]);
      } finally {
        setDistrictLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateField]
  );

  const fetchDaily = useCallback(async (
    dr: [dayjs.Dayjs, dayjs.Dayjs] | null,
    df?: string
  ) => {
    setDailyLoading(true);
    try {
      const params = new URLSearchParams({ ...toDateParams(dr), dateField: df ?? dateField });
      const res: any = await GET(
        `${API.EPAPER_USERS_REPORT_DAILY}?${params}`,
        null, null
      );
      setDailyData(res?.data ?? []);
    } catch {
      setDailyData([]);
    } finally {
      setDailyLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateField]);

  const fetchTable = useCallback(async (
    dr: [dayjs.Dayjs, dayjs.Dayjs] | null,
    df?: string
  ) => {
    setTableLoading(true);
    try {
      const params = new URLSearchParams({ ...toDateParams(dr), dateField: df ?? dateField });
      const res: any = await GET(
        `${API.EPAPER_USERS_REPORT_TABLE}?${params}`,
        null, null
      );
      setTableData(res ?? null);
    } catch {
      setTableData(null);
    } finally {
      setTableLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateField]);

  // Initial load
  useEffect(() => {
    fetchZoneWise(THIS_WEEK);
    fetchDistrictWise(THIS_WEEK, "");
    fetchDaily(THIS_WEEK);
    fetchTable(THIS_WEEK);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ──────────────────── Navigation ────────────────────────────────

  const goToListing = (params: Record<string, string>) => {
    navigate(`/Auth/epaper-users?${new URLSearchParams(params).toString()}`);
  };

  // ──────────────────── Chart click handlers ──────────────────────

  const handleZoneClick = (chartData: any) => {
    const zone = chartData?.activePayload?.[0]?.payload?.zone;
    if (!zone) return;
    goToListing({ agentZone: zone, ...toDateParams(zoneWiseDateRange) });
  };

  const handleDistrictClick = (chartData: any) => {
    const item = chartData?.activePayload?.[0]?.payload as DistrictWiseItem | undefined;
    if (!item?.district) return;
    goToListing({
      agentDistrict: item.district,
      ...(item.zone ? { agentZone: item.zone } : {}),
      ...toDateParams(districtWiseDateRange),
    });
  };

  const handleDailyClick = (chartData: any) => {
    const date = chartData?.activePayload?.[0]?.payload?.date as string | undefined;
    if (!date) return;
    goToListing({
      startDate: dayjs(date).startOf("day").toISOString(),
      endDate: dayjs(date).endOf("day").toISOString(),
    });
  };

  // ──────────────────── Table helpers ────────────────────────────

  /** Returns ordered list of date strings for current table range */
  const getTableDateCols = (): string[] => {
    if (!tableData?.dateRange) return [];
    const cols: string[] = [];
    let cur = dayjs(tableData.dateRange.startDate);
    const end = dayjs(tableData.dateRange.endDate);
    while (!cur.isAfter(end)) {
      cols.push(cur.format("YYYY-MM-DD"));
      cur = cur.add(1, "day");
    }
    return cols;
  };

  /** Transforms flat API rows into ProcessedRow[] with rowSpan values for zone column */
  const processTableRows = (): ProcessedRow[] => {
    if (!tableData?.data?.length) return [];

    const rows: ProcessedRow[] = [];
    let key = 0;

    // Group by zone preserving order
    const zoneOrder: string[] = [];
    const zoneGroups: Record<string, TableDistrictRow[]> = {};
    for (const r of tableData.data) {
      if (!zoneGroups[r.zone]) {
        zoneOrder.push(r.zone);
        zoneGroups[r.zone] = [];
      }
      zoneGroups[r.zone].push(r);
    }

    for (const zone of zoneOrder) {
      const districtRows = zoneGroups[zone];
      // district rows — first gets rowSpan spanning all districts + zone total row
      districtRows.forEach((r, i) => {
        rows.push({
          key: String(key++),
          zone: r.zone,
          district: r.district,
          countBefore: r.countBefore,
          dailyCounts: r.dailyCounts,
          districtTotal: r.districtTotal,
          zoneRowSpan: i === 0 ? districtRows.length + 1 : 0,
        });
      });
      // sum daily counts across all districts for this zone
      const zoneDailyCounts: Record<string, number> = {};
      for (const r of districtRows) {
        for (const [date, cnt] of Object.entries(r.dailyCounts ?? {})) {
          zoneDailyCounts[date] = (zoneDailyCounts[date] ?? 0) + cnt;
        }
      }
      // zone total row
      rows.push({
        key: String(key++),
        zone,
        district: "",
        countBefore: 0,
        dailyCounts: zoneDailyCounts,
        districtTotal: 0,
        isZoneTotal: true,
        zoneTotal: tableData.zoneWiseTotals?.[zone] ?? 0,
        zoneRowSpan: 0,
      });
    }

    return rows;
  };

  const buildTableColumns = () => {
    const dateCols = getTableDateCols();

    const columns: any[] = [
      {
        title: "Zone",
        dataIndex: "zone",
        key: "zone",
        fixed: "left" as const,
        width: 140,
        onCell: (record: ProcessedRow) => ({ rowSpan: record.zoneRowSpan }),
        render: (text: string) => (
          <span
            className={text ? "report-link" : undefined}
            onClick={() =>
              text &&
              goToListing({
                agentZone: text,
                ...toDateParams(tableDateRange),
              })
            }
          >
            {text}
          </span>
        ),
      },
      {
        title: "District",
        dataIndex: "district",
        key: "district",
        fixed: "left" as const,
        width: 150,
        render: (text: string, record: ProcessedRow) => {
          if (record.isZoneTotal) {
            return <strong style={{ color: "#fa8c16" }}>Zone Total</strong>;
          }
          return (
            <span
              className={text ? "report-link" : undefined}
              onClick={() =>
                text &&
                goToListing({
                  agentDistrict: text,
                  agentZone: record.zone,
                  ...toDateParams(tableDateRange),
                })
              }
            >
              {text}
            </span>
          );
        },
      },
      {
        title: "Before Start Date",
        key: "countBefore",
        width: 130,
        align: "center" as const,
        render: (_: any, record: ProcessedRow) => {
          if (record.isZoneTotal) return null;
          return record.countBefore > 0 ? (
            <Tag color="purple">{record.countBefore}</Tag>
          ) : (
            <span style={{ color: "#bbb" }}>0</span>
          );
        },
      },
      // dynamic date columns
      ...dateCols.map((date) => ({
        title: (
          <span style={{ fontSize: 11 }}>{dayjs(date).format("DD/MM")}</span>
        ),
        key: date,
        width: 56,
        align: "center" as const,
        render: (_: any, record: ProcessedRow) => {
          const count = record.dailyCounts?.[date] ?? 0;
          if (record.isZoneTotal) {
            if (count === 0) return <span style={{ color: "#ddd" }}>-</span>;
            return (
              <strong style={{ color: "#fa8c16", cursor: "pointer" }}
                onClick={() =>
                  goToListing({
                    agentZone: record.zone,
                    startDate: dayjs(date).startOf("day").toISOString(),
                    endDate: dayjs(date).endOf("day").toISOString(),
                  })
                }
              >
                {count}
              </strong>
            );
          }
          if (count === 0) return <span style={{ color: "#ddd" }}>-</span>;
          return (
            <Tooltip title={`${record.district} · ${dayjs(date).format("DD MMM YYYY")}`}>
              <span
                className="report-link"
                style={{ fontWeight: 600 }}
                onClick={() =>
                  goToListing({
                    agentDistrict: record.district,
                    agentZone: record.zone,
                    startDate: dayjs(date).startOf("day").toISOString(),
                    endDate: dayjs(date).endOf("day").toISOString(),
                  })
                }
              >
                {count}
              </span>
            </Tooltip>
          );
        },
      })),
      {
        title: "District Total",
        key: "districtTotal",
        width: 110,
        align: "center" as const,
        render: (_: any, record: ProcessedRow) => {
          if (record.isZoneTotal) return null;
          return (
            <Tag
              color="blue"
              style={{ cursor: "pointer" }}
              onClick={() =>
                goToListing({
                  agentDistrict: record.district,
                  agentZone: record.zone,
                  ...toDateParams(tableDateRange),
                })
              }
            >
              {record.districtTotal}
            </Tag>
          );
        },
      },
      {
        title: "Zone Total",
        key: "zoneTotal",
        width: 100,
        align: "center" as const,
        render: (_: any, record: ProcessedRow) => {
          if (!record.isZoneTotal) return null;
          return (
            <Tag
              color="orange"
              style={{ cursor: "pointer", fontWeight: 700 }}
              onClick={() =>
                goToListing({
                  agentZone: record.zone,
                  ...toDateParams(tableDateRange),
                })
              }
            >
              {record.zoneTotal}
            </Tag>
          );
        },
      },
    ];

    return columns;
  };

  // ──────────────────── Excel export ─────────────────────────────

  const handleExportTable = () => {
    if (!tableData?.data?.length) return;
    setExportLoading(true);
    try {
      const dateCols = getTableDateCols();
      const headers = [
        "Zone",
        "District",
        "Before Start Date",
        ...dateCols.map((d) => dayjs(d).format("DD/MM/YYYY")),
        "District Total",
        "Zone Total",
      ];

      const sheetRows: any[][] = [];
      const zoneOrder: string[] = [];
      const zoneGroups: Record<string, TableDistrictRow[]> = {};
      for (const r of tableData.data) {
        if (!zoneGroups[r.zone]) {
          zoneOrder.push(r.zone);
          zoneGroups[r.zone] = [];
        }
        zoneGroups[r.zone].push(r);
      }

      for (const zone of zoneOrder) {
        for (const r of zoneGroups[zone]) {
          sheetRows.push([
            zone,
            r.district,
            r.countBefore,
            ...dateCols.map((d) => r.dailyCounts?.[d] ?? 0),
            r.districtTotal,
            "",
          ]);
        }
        sheetRows.push([
          zone,
          "Zone Total",
          "",
          ...dateCols.map(() => ""),
          "",
          tableData.zoneWiseTotals?.[zone] ?? 0,
        ]);
      }

      const ws = XLSX.utils.aoa_to_sheet([headers, ...sheetRows]);
      // Bold the header row
      for (let c = 0; c < headers.length; c++) {
        const cell = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[cell]) ws[cell].s = { font: { bold: true } };
      }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "E-Paper Report");
      XLSX.writeFile(wb, `epaper-report-${dayjs().format("YYYY-MM-DD")}.xlsx`);
      message.success("Exported to Excel successfully");
    } catch {
      message.error("Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  // ──────────────────── Shared export helper ────────────────────

  const fetchUsersForExport = async (
    extraParams: Record<string, string>
  ): Promise<any[]> => {
    const res: any = await GET(
      `${API.EPAPER_USERS_EXPORT}?${new URLSearchParams({ ...extraParams, dateField })}`,
      null,
      null
    );
    return res?.data ?? [];
  };

  const boldHeaders = (ws: any, count: number) => {
    for (let c = 0; c < count; c++) {
      const cell = XLSX.utils.encode_cell({ r: 0, c });
      if (ws[cell]) ws[cell].s = { font: { bold: true } };
    }
  };

  const safeSheetName = (name: string) =>
    name.replace(/[:\\/?*[\]]/g, "-").slice(0, 31);

  const addUsersSheet = (wb: any, sheetName: string, rows: any[]) => {
    if (rows.length === 0) {
      const ws = XLSX.utils.aoa_to_sheet([["No users in this period"]]);
      XLSX.utils.book_append_sheet(wb, ws, safeSheetName(sheetName));
      return;
    }
    const renumbered = rows.map((r, i) => ({ ...r, "S.No": i + 1 }));
    const ws = XLSX.utils.json_to_sheet(renumbered);
    boldHeaders(ws, Object.keys(renumbered[0]).length);
    XLSX.utils.book_append_sheet(wb, ws, safeSheetName(sheetName));
  };

  // ── Zone-wise export ──

  const handleExportZoneWise = async () => {
    setZoneExportLoading(true);
    try {
      const dateParams = toDateParams(zoneWiseDateRange);
      const allUsers = await fetchUsersForExport(dateParams);

      const wb = XLSX.utils.book_new();

      // Sheet 1 – Summary
      const summaryHeaders = ["Zone", "Count"];
      const summaryRows = zoneWiseData.map((z) => [z.zone, z.count]);
      const summaryWs = XLSX.utils.aoa_to_sheet([summaryHeaders, ...summaryRows]);
      boldHeaders(summaryWs, 2);
      XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

      // Group users by Agent Zone
      const grouped: Record<string, any[]> = {};
      for (const u of allUsers) {
        const zone = u["Agent Zone"] || "Unknown";
        if (!grouped[zone]) grouped[zone] = [];
        grouped[zone].push(u);
      }

      // One sheet per zone (in the same order as the summary)
      for (const { zone } of zoneWiseData) {
        addUsersSheet(wb, zone, grouped[zone] ?? []);
      }

      XLSX.writeFile(wb, `zone-wise-report-${dayjs().format("YYYY-MM-DD")}.xlsx`);
      message.success("Zone-wise report exported");
    } catch {
      message.error("Export failed");
    } finally {
      setZoneExportLoading(false);
    }
  };

  // ── District-wise export ──

  const handleExportDistrictWise = async () => {
    setDistrictExportLoading(true);
    try {
      const dateParams: Record<string, string> = toDateParams(districtWiseDateRange);
      if (districtZoneFilter) dateParams.agentZone = districtZoneFilter;
      const allUsers = await fetchUsersForExport(dateParams);

      const wb = XLSX.utils.book_new();

      // Sheet 1 – Summary
      const summaryHeaders = ["District", "Count"];
      const summaryRows = districtWiseData.map((d) => [d.district, d.count]);
      const summaryWs = XLSX.utils.aoa_to_sheet([summaryHeaders, ...summaryRows]);
      boldHeaders(summaryWs, 2);
      XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

      // Group users by Agent District
      const grouped: Record<string, any[]> = {};
      for (const u of allUsers) {
        const district = u["Agent District"] || "Unknown";
        if (!grouped[district]) grouped[district] = [];
        grouped[district].push(u);
      }

      for (const { district } of districtWiseData) {
        addUsersSheet(wb, district, grouped[district] ?? []);
      }

      XLSX.writeFile(wb, `district-wise-report-${dayjs().format("YYYY-MM-DD")}.xlsx`);
      message.success("District-wise report exported");
    } catch {
      message.error("Export failed");
    } finally {
      setDistrictExportLoading(false);
    }
  };

  // ── Daily export ──

  const handleExportDaily = async () => {
    setDailyExportLoading(true);
    try {
      const rows = await fetchUsersForExport(toDateParams(dailyDateRange));
      if (!rows.length) { message.warning("No users in selected period"); return; }

      const renumbered = rows.map((r: any, i: number) => ({ ...r, "S.No": i + 1 }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(renumbered);
      boldHeaders(ws, Object.keys(renumbered[0]).length);
      XLSX.utils.book_append_sheet(wb, ws, "Users");
      XLSX.writeFile(wb, `daily-users-${dayjs().format("YYYY-MM-DD")}.xlsx`);
      message.success("Daily users exported");
    } catch {
      message.error("Export failed");
    } finally {
      setDailyExportLoading(false);
    }
  };

  // ──────────────────── Render ─────────────────────────────────

  const processedTableRows = processTableRows();
  const tableCols = buildTableColumns();

  return (
    <div className="p-4">
      <style>{`
        .report-link {
          color: #1890ff;
          cursor: pointer;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .report-link:hover { color: #40a9ff; }
        .zone-total-row > td {
          background-color: #fffbe6 !important;
        }
        .chart-hint {
          font-size: 12px;
          color: #aaa;
          margin-top: 6px;
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <PageHeader
          title="E-Paper Agent Performance Reports"
          loader={0}
          length="Dashboard / E-Paper Users / Reports"
        />
        <Space style={{ marginTop: 4 }}>
          <Select
            value={dateField}
            onChange={(v: 'registration' | 'subscription') => {
              setDateField(v);
              fetchZoneWise(zoneWiseDateRange, v);
              fetchDistrictWise(districtWiseDateRange, districtZoneFilter, v);
              fetchDaily(dailyDateRange, v);
              fetchTable(tableDateRange, v);
            }}
            options={[
              { value: 'registration', label: 'Registrations' },
              { value: 'subscription', label: 'Subscriptions' },
            ]}
            style={{ width: 175 }}
          />
        </Space>
      </div>
      <br />

      {/* ── Daily Chart ── */}
      <Card
        title="Daily Users"
        style={{ marginBottom: 16 }}
        extra={
          <Space size={8}>
            <DatePresetFilter
              value={dailyDateRange}
              defaultPreset="this_week"
              onChange={setDailyDateRange}
              onApply={(dr) => fetchDaily(dr, dateField)}
            />
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={dailyExportLoading}
              onClick={handleExportDaily}
            >
              Export
            </Button>
          </Space>
        }
      >
        {dailyLoading ? (
          <Skeleton active paragraph={{ rows: 7 }} title={false} style={{ padding: "12px 0" }} />
        ) : dailyData.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data available" />
        ) : (
          <ResponsiveContainer width="100%" height={320}>
              <AreaChart
                data={dailyData}
                onClick={handleDailyClick}
                style={{ cursor: "pointer" }}
                margin={{ top: 24, right: 24, left: 0, bottom: 8 }}
              >
                <defs>
                  <linearGradient id="dailyGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1890ff" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#1890ff" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(d) => dayjs(d).format("DD/MM")}
                />
                <YAxis allowDecimals={false} />
                <RechartsTooltip
                  formatter={(val: any) => [val, "Users"]}
                  labelFormatter={(d) => dayjs(d as string).format("DD MMM YYYY")}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  name="Users"
                  stroke="#1890ff"
                  strokeWidth={2}
                  fill="url(#dailyGradient)"
                  dot={{ r: 4, fill: "#1890ff", strokeWidth: 0, cursor: "pointer" }}
                  activeDot={{ r: 6 }}
                >
                  <LabelList
                    dataKey="count"
                    position="top"
                    style={{ fontSize: 11, fill: "#555", fontWeight: 600 }}
                  />
                </Area>
              </AreaChart>
            </ResponsiveContainer>
        )}
        <p className="chart-hint">Click any point to view users created on that date.</p>
      </Card>

      {/* ── Zone-wise Chart ── */}
      <Card
        title="Zone-wise User"
        style={{ marginBottom: 16 }}
        extra={
          <Space size={8}>
            <DatePresetFilter
              value={zoneWiseDateRange}
              defaultPreset="this_week"
              onChange={setZoneWiseDateRange}
              onApply={(dr) => fetchZoneWise(dr, dateField)}
            />
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={zoneExportLoading}
              onClick={handleExportZoneWise}
            >
              Export
            </Button>
          </Space>
        }
      >
        {zoneLoading ? (
          <Skeleton active paragraph={{ rows: 7 }} title={false} style={{ padding: "12px 0" }} />
        ) : zoneWiseData.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data available" />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={zoneWiseData}
              onClick={handleZoneClick}
              style={{ cursor: "pointer" }}
              margin={{ top: 24, right: 24, left: 0, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="zone"
                interval={0}
                tick={{ fontSize: 10, angle: -35, textAnchor: "end" }}
                height={70}
              />
              <YAxis allowDecimals={false} />
              <RechartsTooltip
                formatter={(val: any) => [val, "Users"]}
                cursor={{ fill: "rgba(24,144,255,0.08)" }}
              />
              <Bar dataKey="count" name="Users" radius={[4, 4, 0, 0]} maxBarSize={30}>
                {zoneWiseData.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
                <LabelList
                  dataKey="count"
                  position="top"
                  style={{ fontSize: 11, fill: "#555", fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
        <p className="chart-hint">Click any bar to view users for that zone.</p>
      </Card>

      {/* ── District-wise Chart ── */}
      <Card
        title="District-wise User"
        style={{ marginBottom: 16 }}
        extra={
          <Space size="small" wrap>
            <DatePresetFilter
              value={districtWiseDateRange}
              defaultPreset="this_week"
              onChange={setDistrictWiseDateRange}
              onApply={(dr) => fetchDistrictWise(dr, districtZoneFilter, dateField)}
            />
            <Select
              size="small"
              style={{ width: 160 }}
              placeholder="All Zones"
              allowClear
              value={districtZoneFilter || undefined}
              onChange={(val) => {
                const z = val ?? "";
                setDistrictZoneFilter(z);
                fetchDistrictWise(districtWiseDateRange, z, dateField);
              }}
            >
              {zones.map((z) => (
                <Option key={z} value={z}>{z}</Option>
              ))}
            </Select>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={districtExportLoading}
              onClick={handleExportDistrictWise}
            >
              Export
            </Button>
          </Space>
        }
      >
        {districtLoading ? (
          <Skeleton active paragraph={{ rows: 7 }} title={false} style={{ padding: "12px 0" }} />
        ) : districtWiseData.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data available" />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={districtWiseData}
              onClick={handleDistrictClick}
              style={{ cursor: "pointer" }}
              margin={{ top: 24, right: 24, left: 0, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="district"
                interval={0}
                tick={{ fontSize: 10, angle: -35, textAnchor: "end" }}
                height={70}
              />
              <YAxis allowDecimals={false} />
              <RechartsTooltip
                formatter={(val: any) => [val, "Users"]}
                cursor={{ fill: "rgba(24,144,255,0.08)" }}
              />
              <Bar dataKey="count" name="Users" radius={[4, 4, 0, 0]} maxBarSize={30}>
                {districtWiseData.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
                <LabelList
                  dataKey="count"
                  position="top"
                  style={{ fontSize: 11, fill: "#555", fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
        <p className="chart-hint">Click any bar to view users for that district.</p>
      </Card>

      {/* ── Detailed Table Report ── */}
      <Card
        title="Detailed Table Report"
        extra={
          <Space wrap>
            <DatePresetFilter
              value={tableDateRange}
              defaultPreset="this_week"
              onChange={setTableDateRange}
              onApply={(dr) => fetchTable(dr, dateField)}
            />
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={handleExportTable}
              loading={exportLoading}
              disabled={!tableData?.data?.length}
            >
              Export Excel
            </Button>
          </Space>
        }
      >
        {tableLoading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : processedTableRows.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No table data available" />
        ) : (
          <Table
            dataSource={processedTableRows}
            columns={tableCols}
            pagination={false}
            scroll={{ x: "max-content" }}
            size="small"
            bordered
            rowClassName={(record: ProcessedRow) =>
              record.isZoneTotal ? "zone-total-row" : ""
            }
          />
        )}
        <p className="chart-hint" style={{ marginTop: 8 }}>
          Data from agents. Click zone, district, date count, or totals to view filtered users.
        </p>
      </Card>
    </div>
  );
};

export default EpaperUsersReports;
